import { useEffect, useState } from "react";
import { Loader2, Mail, Trash2, UserPlus } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { emailProblem } from "@/lib/emailGuard";
import { InviteStatus } from "@/components/InviteStatus";
import { Input } from "@/components/ui/input";

const ROLES = [
  { key: "manager", label: "Manager — runs operations, no billing or company settings" },
  { key: "dispatcher", label: "Dispatcher — trips, dispatch and customers" },
  { key: "accountant", label: "Accountant — payments, invoices, payouts and reports" },
  { key: "driver", label: "Driver — sees only their own assigned trips" },
  { key: "client", label: "Client — books trips and views their invoices" },
];

const ROLE_STYLE = {
  owner: "border-gold/40 bg-gold/10 text-gold",
  manager: "border-purple-500/40 bg-purple-500/10 text-purple-300",
  dispatcher: "border-blue-500/40 bg-blue-500/10 text-blue-300",
  accountant: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  driver: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  client: "border-slate-500/40 bg-slate-500/10 text-slate-300",
};

const PERMISSION_LABELS = {
  trips: "Manifests & trips",
  dispatch: "Dispatch & live map",
  vehicles: "Vehicles & chauffeurs",
  pricing: "Pricing rules",
  payments: "Payments & invoices",
  customers: "Customer records",
  reports: "Reports & revenue",
  team: "Users & permissions",
  settings: "Company settings",
  affiliates: "Affiliate network",
  payouts: "Chauffeur payouts",
};

const CONSOLE_ROLES = ["manager", "dispatcher", "accountant"];

export default function Team() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "manager" });
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/team").then(({ data }) => {
    setItems(data.members);
    setPermissions(data.permissions);
  }).catch((e) => setError(errMsg(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount; re-called by hand after invites and edits
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setMsg("");
    setBusy(true);
    try {
      const { data } = await api.post("/team/invite", form);
      setMsg(`Invitation created for ${data.email}. Delivery shows on their card below — if the email `
        + `bounces you can resend or copy their link.`);
      setForm({ name: "", email: "", phone: "", role: "manager" });
      load();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="team-page" className="space-y-8">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">Access</p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-50">Portals & team</h1>
        <p className="mt-2 text-sm text-slate-400">
          One login, many portals. You decide exactly what each person can reach — managers run operations,
          dispatchers handle the road, accountants see the money, drivers see only their own trips and clients
          see only their own bookings.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <form onSubmit={submit} className="space-y-5 rounded-xl border border-ink-600 bg-ink-800/60 p-6 lg:col-span-2">
          <h2 className="text-lg font-medium text-slate-100">Invite to a portal</h2>
          {["name", "email", "phone"].map((k) => (
            <div key={k}>
              <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">{k}</label>
              <Input
                data-testid={`team-${k}`}
                required={k !== "phone"}
                type={k === "email" ? "email" : "text"}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="h-11 border-ink-600 bg-ink-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold"
              />
              {k === "email" && emailProblem(form.email) && (
                <p data-testid="team-email-warning" className="mt-2 text-xs text-amber-300">
                  {emailProblem(form.email)}
                </p>
              )}
            </div>
          ))}
          <div>
            <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">Portal role</label>
            <select
              data-testid="team-role"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="h-11 w-full rounded-md border border-ink-600 bg-ink-800 px-3 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-gold"
            >
              {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          {msg && <p data-testid="team-success" className="text-sm text-emerald-400">{msg}</p>}
          {error && <p data-testid="team-error" className="text-sm text-red-400">{error}</p>}
          <Button data-testid="team-invite-btn" type="submit" disabled={busy || !!emailProblem(form.email)} className="h-11 w-full bg-gold text-ink-900 hover:bg-gold-light">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><UserPlus className="mr-2 h-4 w-4" /> Send invitation</>)}
          </Button>
        </form>

        <div className="space-y-3 lg:col-span-3">
          {items.map((m) => (
            <div key={m.id} data-testid={`team-card-${m.id}`} className="card-hover flex items-start justify-between gap-4 rounded-xl border border-ink-600 bg-ink-800 p-5">
              <div className="min-w-0">
                <p className="truncate text-base font-medium text-slate-100">{m.name}</p>
                <p className="mt-1.5 flex items-center gap-1.5 truncate text-xs text-slate-400">
                  <Mail className="h-3 w-3" /> {m.email}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${ROLE_STYLE[m.role]}`}>
                    {m.role}
                  </span>                  {CONSOLE_ROLES.includes(m.role) && (
                    <span data-testid={`team-perm-count-${m.id}`} className="font-mono text-[10px] text-slate-500">
                      {m.permissions.length} of {permissions.length} areas
                    </span>
                  )}
                </div>
                <InviteStatus member={m} onDone={load} />
                {CONSOLE_ROLES.includes(m.role) && (
                  <div className="mt-3">
                    {editing === m.id ? (
                      <div className="space-y-3 rounded-lg border border-ink-600 bg-ink-900/60 p-4">
                        {permissions.map((p) => (
                          <label key={p} data-testid={`perm-toggle-${p}-${m.id}`}
                            className="flex cursor-pointer items-center gap-3 text-xs text-slate-300">
                            <input type="checkbox" checked={Boolean(draft[p])}
                              onChange={(e) => setDraft({ ...draft, [p]: e.target.checked })}
                              className="h-4 w-4 accent-gold" />
                            {PERMISSION_LABELS[p] || p}
                          </label>
                        ))}
                        <div className="flex gap-2 pt-1">
                          <Button data-testid={`perm-save-${m.id}`} disabled={busy}
                            className="h-9 bg-gold text-ink-900 hover:bg-gold-light"
                            onClick={async () => {
                              setBusy(true); setError(""); setMsg("");
                              try {
                                await api.put(`/team/${m.id}/permissions`, { permissions: draft });
                                setMsg(`Access updated for ${m.name}.`);
                                setEditing(null); load();
                              } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
                            }}>
                            Save access
                          </Button>
                          <Button variant="ghost" className="h-9 text-slate-400" onClick={() => setEditing(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <button data-testid={`perm-edit-${m.id}`} className="font-mono text-[11px] text-gold"
                        onClick={() => {
                          setEditing(m.id);
                          setDraft(Object.fromEntries(permissions.map((p) => [p, m.permissions.includes(p)])));
                        }}>
                        Edit what they can access →
                      </button>
                    )}
                  </div>
                )}
              </div>
              {m.role !== "owner" && m.id !== user?.id && (
                <button
                  data-testid={`team-delete-${m.id}`}
                  onClick={async () => { await api.delete(`/team/${m.id}`); load(); }}
                  className="text-slate-500 transition-colors hover:text-red-400"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
